import React, { useEffect, useRef } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import RichTextViewer from '@/components/RichTextViewer';
import { renderDeleteAction } from '@/modules/corporate/modules/questions/fields/components/Editor/OptionsListItem/renderers';
import RichTextEditor from '@/modules/corporate/modules/questions/fields/components/Editor/RichTextEditor';
import { optionCodeStyles } from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/styles';
import useMtfEditorOptionsListItem from '@/modules/corporate/modules/questions/fields/match-the-following/hooks/useMtfEditorOptionsListItem';
import { renderArrow } from '@/modules/corporate/modules/questions/fields/match-the-following/Player/components/ElementsList/renderers';

import { MtfOptionListItemProps } from '../../types';

const MtfOptionsListItem = (props: MtfOptionListItemProps) => {
  const {
    viewMode,
    index,
    onAction,
    leftElement,
    rightElement,
    isEditable = viewMode !== 'view',
    isDeletable = viewMode !== 'view',
  } = props;

  const { t } = useTranslation();
  const { onRemove, onChangeLeftElementText, onChangeRightElementText } =
    useMtfEditorOptionsListItem({
      index,
      onAction,
      leftElement,
      rightElement,
    });

  const ref = useRef<HTMLDivElement | null>(null);
  const leftElementRef = useRef<HTMLDivElement | null>(null);
  const [isLeftElementFocused, { on, off }] = useToggle(false);

  useEffect(() => {
    const leftElementContainerEl = leftElementRef.current;
    leftElementContainerEl?.addEventListener('focusin', on);
    leftElementContainerEl?.addEventListener('focusout', off);

    return () => {
      leftElementContainerEl?.removeEventListener('focusin', on);
      leftElementContainerEl?.removeEventListener('focusout', off);
    };
  }, [off, on]);

  const leftDataMtfId = `${index}-left`;
  const rightDataMtfId = `${index}-right`;
  const isDisabled = viewMode !== 'create';

  return (
    <Stack ref={ref} alignItems="center" className="relative min-w-0 flex-1">
      <Stack
        ref={leftElementRef}
        alignItems="start"
        className={cn(
          'h-full w-1/2 overflow-hidden rounded-8 border border-divider bg-bg-secondary py-8 focus-within:border-interactive-primary hover:border-border-dark',
          {
            disabled: isDisabled,
          },
        )}
        data-mtf-id={leftDataMtfId}>
        {isEditable ? (
          <RichTextEditor
            className={cn('mx-12 flex-1', optionCodeStyles())}
            placeholder={t('QuizCreator:Clue', { index: index + 1 })}
            value={leftElement.text}
            onChangeText={onChangeLeftElementText}
          />
        ) : (
          <RichTextViewer className="flex-1 ms-12" html={leftElement.text} />
        )}
      </Stack>
      {renderArrow({
        leftId: leftDataMtfId,
        rightId: rightDataMtfId,
        containerEl: ref.current,
        showArrowHead: true,
        showDot: true,
        color: isLeftElementFocused ? 'var(--icon-active)' : 'var(--icon-secondary)',
        strokeWidth: 1.3,
      })}
      <Stack
        alignItems="start"
        className={cn(
          'h-full w-1/2 overflow-hidden rounded-8 border border-divider bg-bg-secondary py-8 ms-32 focus-within:border-interactive-primary hover:border-border-dark',
          {
            disabled: isDisabled,
          },
        )}
        data-mtf-id={rightDataMtfId}>
        {isEditable ? (
          <RichTextEditor
            className={cn('mx-12 flex-1', optionCodeStyles())}
            placeholder={t('QuizCreator:Match', { index: index + 1 })}
            value={rightElement.text}
            onChangeText={onChangeRightElementText}
          />
        ) : (
          <RichTextViewer className="flex-1 ms-12" html={rightElement.text} />
        )}
      </Stack>
      {isDeletable
        ? renderDeleteAction({
            className: 'absolute -end-28',
            onRemove,
            disabled: isDisabled,
          })
        : null}
    </Stack>
  );
};

export default MtfOptionsListItem;
